import { defineStore } from "pinia";
import type { DBTYPE_Currency, DBTYPE_Activity } from "~/backend_types";

import { GENERAL_addEditDelete } from "~/utils/useBackend";

import type { components } from "@/types_backend";

const URL_currency = "/api/masterdata/currency/";
const URL_companyInfo = "/api/masterdata/company-info/";
const URL_getCompanyImage = "/api/masterdata/company-image/";
const URL_activity = "/api/projectmanagement/activity/";
const URL_hourlyRateRule = "/api/projectmanagement/hourly-rate-rule/";

type DjangoResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const useMasterDataStore = defineStore("masterdata", {
  state: () => ({
    companyInfo: null as components["schemas"]["CompanyInfo"] | null,
    activities: null as DjangoResponse<DBTYPE_Activity> | null,
    hourlyRateRules: null as DjangoResponse<
      components["schemas"]["HourlyRateRuleOutput"]
    > | null,
    currencies: null as DjangoResponse<DBTYPE_Currency> | null,
    companyImage: null as Blob | null,
  }),
  actions: {
    async fetchMasterData() {
      await Promise.all([
        this.getCompanyInfo(),
        this.getActivities({}),
        this.getHourlyRateRules({}),
        this.getCurrencies({}),
        this.getCompanyImage(),
      ]);
    },

    /* Company Info */
    async getCompanyInfo(): Promise<
      DjangoResponse<components["schemas"]["CompanyInfo"]>
    > {
      const data = (await backendGetRequest(
        URL_companyInfo,
        { page: 1, page_size: 1, sortBy: [] },
        {}
      )) as any;

      this.companyInfo = data.results[0];
      return data;
    },

    async addEditDeleteCompanyInfo(
      action: "add" | "edit" | "delete",
      companyInfo: components["schemas"]["CompanyInfo"]
    ) {
      const data = await GENERAL_addEditDelete(
        action,
        URL_companyInfo,
        companyInfo
      );

      await this.getCompanyInfo();

      return data;
    },
    async uploadCompanyImage(file: File) {
      const formData = new FormData();
      formData.append("id", this.companyInfo?.id as any);
      formData.append("name", this.companyInfo?.name as any);
      formData.append("company_image", file);

      const data = await fetch(`${URL_companyInfo}${this.companyInfo?.id}/`, {
        method: "PATCH",
        headers: getHeaders(),
        body: formData,
      });

      this.getCompanyImage();

      return data;
    },
    async getCompanyImage() {
      try {
        const data = await apiGetRequest(URL_getCompanyImage);

        this.companyImage = data as Blob;
        return data;
      } catch (error) {
        console.log("Error fetching company image: Probably not set yet");
        console.log(error);
      }

      return null;
    },

    /* Currencies */
    async getCurrencyById(id: number) {
      const data = (await apiGetRequest(`${URL_currency}${id}/`)) as any;
      return data;
    },
    async getCurrencies(
      {
        page = 1,
        page_size = 10,
        sortBy = [] as { key: string; order: "asc" | "desc" }[],
      },
      filter: any = {},
      search: string = ""
    ): Promise<DjangoResponse<DBTYPE_Currency>> {
      const data = (await backendGetRequest(
        URL_currency,
        { page, page_size, sortBy },
        filter,
        search
      )) as any;

      // Ensuring that nothing is written to the stores state which was filtered or searched for (so that the store always contains all currencies)
      if (Object.keys(filter).length === 0 && search === "") {
        this.currencies = data;
      }

      return data;
    },
    async addEditDeleteCurrency(
      action: "add" | "edit" | "delete",
      currency: DBTYPE_Currency,
      refetch_store = true
    ) {
      const data = await GENERAL_addEditDelete(action, URL_currency, currency);

      if (refetch_store) this.getCurrencies({});

      return data;
    },

    /* Activities */
    async getActivityById(id: number) {
      const data = (await apiGetRequest(
        `${URL_activity}${id}/`
      )) as Promise<DBTYPE_Activity>;
      return data;
    },
    async getActivities(
      { sortBy = [] as { key: string; order: "asc" | "desc" }[] },
      filter: any = {},
      search: string = ""
    ) {
      const data = (await backendGetRequest(
        URL_activity,
        { page: 1, page_size: 1000, sortBy },
        filter,
        search
      )) as any;

      // Ensuring that nothing is written to the stores state which was filtered or searched for (so that the store always contains all activities)
      if (Object.keys(filter).length === 0 && search === "") {
        this.activities = data;
      }

      return data;
    },
    async addEditDeleteActivity(
      action: "add" | "edit" | "delete",
      activity: DBTYPE_Activity,
      refetch_store = true
    ) {
      const data = await GENERAL_addEditDelete(action, URL_activity, activity);

      if (refetch_store) this.getActivities({});

      return data;
    },

    /* Hourly Rates */
    async getHourlyRateRuleById(id: number) {
      const data = (await apiGetRequest(
        `${URL_hourlyRateRule}${id}/`
      )) as components["schemas"]["HourlyRateRuleOutput"];

      return data;
    },
    async getHourlyRateRules(
      { sortBy = [] as { key: string; order: "asc" | "desc" }[] },
      filter: any = {},
      search: string = ""
    ) {
      const data = (await backendGetRequest(
        `${URL_hourlyRateRule}`,
        { page: 1, page_size: 1000, sortBy },
        filter,
        search
      )) as DjangoResponse<components["schemas"]["HourlyRateRuleOutput"]>;

      // Ensuring that nothing is written to the stores state which was filtered or searched for (so that the store always contains all activities)
      if (Object.keys(filter).length === 0 && search === "") {
        this.hourlyRateRules = data;
      }

      return data;
    },
    async addEditDeleteHourlyRateRule(
      action: "add" | "edit" | "delete",
      hourlyRate: components["schemas"]["HourlyRateRuleInput"],
      refetch_store = true
    ) {
      const data = await GENERAL_addEditDelete(
        action,
        `${URL_hourlyRateRule}`,
        hourlyRate
      );

      if (refetch_store) this.getHourlyRateRules({});

      return data;
    },
  },
});
